import type { MerchantApiClient } from "../../types.js";
import { dispatchMerchantTelemetry } from "../../shared/telemetry.js";
import { createTeamAccessApi, type TeamAccessApi } from "./api.js";
import type {
  RoleAssignmentDraft,
  StatusChangeDraft,
  TeamAccessFilters,
  TeamAccessPage,
  TeamUser,
} from "./models.js";

export interface TeamAccessTelemetry {
  track(eventName: string, payload: Record<string, string | number | boolean | undefined>): void;
}

export interface TeamAccessService {
  assignRole(user: TeamUser, draft: RoleAssignmentDraft): Promise<TeamUser>;
  loadUser(userId: string): Promise<TeamUser>;
  loadUsers(filters: TeamAccessFilters): Promise<TeamAccessPage>;
  updateStatus(user: TeamUser, draft: StatusChangeDraft): Promise<TeamUser>;
}

export interface TeamAccessServiceOptions {
  api?: TeamAccessApi;
  apiClient?: MerchantApiClient;
  idempotencyKeyFactory?: () => string;
  telemetry?: TeamAccessTelemetry;
}

export function createTeamAccessService(options: TeamAccessServiceOptions): TeamAccessService {
  const api = options.api ?? createApiFromClient(options.apiClient);
  const telemetry = options.telemetry ?? browserTelemetry();
  const idempotencyKeyFactory = options.idempotencyKeyFactory ?? (() => crypto.randomUUID());

  return {
    assignRole(user, draft) {
      return trackRoleAssignment(
        telemetry,
        () => api.assignRole(user.userId, draft, idempotencyKeyFactory()),
        user,
        draft,
      );
    },
    loadUser(userId) {
      return track(telemetry, "merchant_team_user_loaded", () => api.getUser(userId), { userId });
    },
    loadUsers(filters) {
      return track(telemetry, "merchant_team_users_loaded", () => api.listUsers(filters), {
        role: filters.role || undefined,
        status: filters.status || undefined,
      });
    },
    updateStatus(user, draft) {
      return track(
        telemetry,
        "merchant_team_status_changed",
        () => api.updateStatus(user.userId, draft, idempotencyKeyFactory()),
        { status: draft.status, userId: user.userId },
      );
    },
  };
}

async function trackRoleAssignment(
  telemetry: TeamAccessTelemetry,
  operation: () => Promise<TeamUser>,
  user: TeamUser,
  draft: RoleAssignmentDraft,
): Promise<TeamUser> {
  const result = await track(telemetry, "merchant_team_role_assigned", operation, {
    role: draft.role,
    userId: user.userId,
  });
  telemetry.track("merchant_role_changed", {
    role: draft.role,
    userId: user.userId,
    result: "success",
  });
  return result;
}

async function track<T>(
  telemetry: TeamAccessTelemetry,
  eventName: string,
  operation: () => Promise<T>,
  payload: Record<string, string | number | boolean | undefined>,
): Promise<T> {
  const startedAt = performance.now();
  try {
    const result = await operation();
    telemetry.track(eventName, {
      ...payload,
      result: "success",
      durationMs: Math.round(performance.now() - startedAt),
    });
    return result;
  } catch (error: unknown) {
    telemetry.track(eventName, {
      ...payload,
      result: "failure",
      durationMs: Math.round(performance.now() - startedAt),
      errorName: error instanceof Error ? error.name : "unknown",
    });
    throw error;
  }
}

function createApiFromClient(client: MerchantApiClient | undefined): TeamAccessApi {
  if (client === undefined) {
    throw new Error("Team access service requires an API client.");
  }

  return createTeamAccessApi(client);
}

function browserTelemetry(): TeamAccessTelemetry {
  return {
    track(eventName, payload) {
      dispatchMerchantTelemetry(eventName, payload);
    },
  };
}
